import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';

import { isModalOpen } from '@/redux/modals/selectors';
import { selectIframeView } from '@/redux/user/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';

import { useRecentPages } from './hooks/useRecentPages';
import { type RecentPage } from './types';

type CommandPaletteContextValue = {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  openFromTrigger: () => void;
  recentPages: RecentPage[];
};

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/** Returns the command palette open/close state. Must be used within CommandPaletteProvider. */
export const useCommandPaletteContext = () => {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPaletteContext must be used within CommandPaletteProvider');
  }
  return ctx;
};

export const CommandPaletteProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setOpen] = useState(false);
  const iframeView = useSelector(selectIframeView);
  const hasModalOpen = useSelector(isModalOpen);
  const hasModalOpenRef = useRef(hasModalOpen);
  useEffect(() => {
    hasModalOpenRef.current = hasModalOpen;
  }, [hasModalOpen]);
  const { recentPages } = useRecentPages();

  const toggle = useCallback((trigger?: 'keyboard' | 'button') => {
    setOpen((prev) => {
      if (prev) {
        return false;
      }
      if (hasModalOpenRef.current) {
        return false;
      }
      if (trigger) {
        trackEvent(EventName.OpenCommandPalette, { trigger });
      }
      return true;
    });
  }, []);

  const openFromTrigger = useCallback(() => toggle('button'), [toggle]);

  useHotkeys(
    'mod+k',
    (e) => {
      e.preventDefault();
      toggle('keyboard');
    },
    { enableOnFormTags: ['INPUT', 'TEXTAREA', 'SELECT'], enabled: !iframeView },
    [toggle]
  );

  const value = useMemo(
    () => ({ isOpen, setOpen, openFromTrigger, recentPages }),
    [isOpen, openFromTrigger, recentPages]
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
};
